import axios, {
  isAxiosError,
  type AxiosInstance,
  type AxiosResponse,
} from 'axios';

import { HttpClientError } from '@errors/httpClient.error';
import { UnknownError } from '@errors/unknown.error';

import { interceptors } from './interceptors';

class BackendHttpClient {
  private readonly instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: import.meta.env.VITE_CONNECT_BACKEND_URL,
      timeout: 10000,
      paramsSerializer: {
        indexes: null,
      },
    });
    interceptors.setTokens(this.instance);
  }

  public async get<Response = unknown, Params = unknown>({
    headers = {},
    params,
    path,
  }: {
    headers?: Record<string, string>;
    params?: Params;
    path: string;
  }): Promise<AxiosResponse<Response>> {
    try {
      const response = await this.instance.get<Response>(path, {
        params,
        headers: {
          ...headers,
        },
      });
      return response;
    } catch (err) {
      this.throwError(err);
    }
  }

  public async post<Response, Body = unknown>({
    body,
    path,
  }: {
    body?: Body;
    path: string;
  }): Promise<AxiosResponse<Response>> {
    try {
      const response = await this.instance.post<Response>(path, body);
      return response;
    } catch (err) {
      this.throwError(err);
    }
  }

  public async put<Response, Body = unknown, Params = unknown>({
    body,
    params,
    path,
  }: {
    body?: Body;
    params?: Params;
    path: string;
  }): Promise<AxiosResponse<Response>> {
    try {
      const response = await this.instance.put<Response>(path, body, {
        params,
      });
      return response;
    } catch (err) {
      this.throwError(err);
    }
  }

  public async patch<Response, Body = unknown, Params = unknown>({
    body,
    params,
    path,
  }: {
    body?: Body;
    params?: Params;
    path: string;
  }): Promise<AxiosResponse<Response>> {
    try {
      const response = await this.instance.patch<Response>(path, body, {
        params,
      });
      return response;
    } catch (err) {
      this.throwError(err);
    }
  }

  public async delete<Response, Params = unknown>({
    params,
    path,
  }: {
    params?: Params;
    path: string;
  }): Promise<AxiosResponse<Response>> {
    try {
      const response = await this.instance.delete<Response>(path, {
        params,
      });
      return response;
    } catch (err) {
      this.throwError(err);
    }
  }

  public removeBearerToken(): void {
    this.instance.defaults.headers.common.Authorization = '';
  }

  public setBearerToken(token: string): void {
    this.instance.defaults.headers.common.Authorization = `Bearer ${token}`;
  }

  private throwError(err: unknown): never {
    if (isAxiosError(err)) {
      throw new HttpClientError({
        message: `${err.response?.status} - ${err.response?.statusText}`,
        response: err.response,
      });
    }

    throw new UnknownError({
      context: {
        originalError: err,
      },
    });
  }
}

export default new BackendHttpClient();
